import React, { useMemo, useState } from 'react'
import { mockNotifications } from '@assets/mockdata/Notifications/mockNotifications.data'
import { ReactComponent as ToothLogo } from '@assets/images/tooth-ai.svg'
import {
  Header,
  type HeaderHipaaView,
  type HeaderLocationOption,
  type HeaderNotificationItem,
  type HeaderNotificationPriority,
} from '@enamelai/design-system'
import { NotificationPriority } from '@dataTypes/notification.types'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'

const SAMPLE_DATE = new Date(2026, 5, 8)

const mapNotificationPriority = (
  priority: NotificationPriority,
): HeaderNotificationPriority => {
  if (priority === NotificationPriority.CRITICAL) {
    return 'critical'
  }

  if (priority === NotificationPriority.URGENT) {
    return 'urgent'
  }

  return 'low'
}

const getNotificationCategoryLabel = (title: string): string => {
  if (title.includes('Appointment') || title.includes('scheduling')) {
    return 'Appointments'
  }

  if (title.includes('Claim')) {
    return 'Claims'
  }

  if (title.includes('Form')) {
    return 'Forms'
  }

  if (title.includes('Payment')) {
    return 'Billing'
  }

  return 'Charting'
}

const SAMPLE_NOTIFICATIONS: HeaderNotificationItem[] = mockNotifications
  .slice(0, 6)
  .map((notification) => ({
    id: notification.id,
    title: notification.title,
    message: notification.message,
    date: notification.date,
    time: notification.time,
    priority: mapNotificationPriority(notification.priority),
    actionButtonText: notification.actionButtonText,
    category: getNotificationCategoryLabel(notification.title),
  }))

const SAMPLE_LOCATIONS: HeaderLocationOption[] = [
  {
    id: 'xyz-clinic',
    name: 'XYZ Clinic',
    address: '123 Main St, Austin, TX',
  },
  {
    id: 'abc-dental',
    name: 'ABC Dental',
    address: '456 Oak Ave, Dallas, TX',
  },
  {
    id: 'smile-center',
    name: 'Smile Center',
    address: '789 Pine Rd, Houston, TX',
  },
]

const HeaderShowcase: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false)
  const [showSearch, setShowSearch] = useState(true)
  const [showHipaaToggle, setShowHipaaToggle] = useState(true)
  const [showLocation, setShowLocation] = useState(true)
  const [showNotifications, setShowNotifications] = useState(true)
  const [hasNotifications, setHasNotifications] = useState(true)
  const [hipaaView, setHipaaView] = useState<HeaderHipaaView>('view')
  const [searchValue, setSearchValue] = useState('')
  const [selectedLocationId, setSelectedLocationId] = useState('xyz-clinic')
  const [lastAction, setLastAction] = useState('Open a menu to interact.')

  const selectedLocation = SAMPLE_LOCATIONS.find(
    (location) => location.id === selectedLocationId,
  )

  const userMenuItems = useMemo(
    () => [
      {
        id: 'account',
        label: 'Account',
        onSelect: () => setLastAction('Account selected'),
      },
      {
        id: 'logout',
        label: 'Log out',
        onSelect: () => setLastAction('Log out selected'),
      },
    ],
    [],
  )

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'header-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="header-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'header-hipaa-view',
            label: 'HIPAA view',
            control: (
              <select
                id="header-hipaa-view"
                className="ui-field-control"
                value={hipaaView}
                onChange={(event) =>
                  setHipaaView(event.target.value as HeaderHipaaView)
                }
              >
                <option value="view">Show PHI</option>
                <option value="hide">Hide PHI</option>
              </select>
            ),
          },
        ],
      },
      {
        title: 'Sections',
        fields: [
          {
            id: 'header-show-search',
            label: 'Search',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="header-show-search"
                  type="checkbox"
                  checked={showSearch}
                  onChange={(event) => setShowSearch(event.target.checked)}
                />
                <span>Visible</span>
              </label>
            ),
          },
          {
            id: 'header-show-hipaa',
            label: 'HIPAA toggle',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="header-show-hipaa"
                  type="checkbox"
                  checked={showHipaaToggle}
                  onChange={(event) => setShowHipaaToggle(event.target.checked)}
                />
                <span>Visible</span>
              </label>
            ),
          },
          {
            id: 'header-show-location',
            label: 'Location',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="header-show-location"
                  type="checkbox"
                  checked={showLocation}
                  onChange={(event) => setShowLocation(event.target.checked)}
                />
                <span>Visible</span>
              </label>
            ),
          },
          {
            id: 'header-show-notifications',
            label: 'Notifications',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="header-show-notifications"
                  type="checkbox"
                  checked={showNotifications}
                  onChange={(event) =>
                    setShowNotifications(event.target.checked)
                  }
                />
                <span>Visible</span>
              </label>
            ),
          },
          {
            id: 'header-has-notifications',
            label: 'Notification badge',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="header-has-notifications"
                  type="checkbox"
                  checked={hasNotifications}
                  onChange={(event) =>
                    setHasNotifications(event.target.checked)
                  }
                />
                <span>Unread</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      darkMode,
      hasNotifications,
      hipaaView,
      showHipaaToggle,
      showLocation,
      showNotifications,
      showSearch,
    ],
  )

  const dropdownHeaderProps = {
    locations: SAMPLE_LOCATIONS,
    selectedLocationId,
    onLocationSelect: (locationId: string) => {
      setSelectedLocationId(locationId)
      const location = SAMPLE_LOCATIONS.find((item) => item.id === locationId)
      setLastAction(`Location changed to ${location?.name ?? locationId}`)
    },
    notifications: SAMPLE_NOTIFICATIONS,
    onMarkAllNotificationsRead: () => {
      setHasNotifications(false)
      setLastAction('All notifications marked as read')
    },
    onViewAllNotifications: () => setLastAction('View more notifications'),
    onExpandNotifications: () => setLastAction('Expand notifications'),
    onNotificationToggleRead: (notificationId: string, isRead: boolean) => {
      const notification = SAMPLE_NOTIFICATIONS.find(
        (item) => item.id === notificationId,
      )
      setLastAction(
        `${isRead ? 'Marked read' : 'Marked unread'}: ${notification?.title ?? notificationId}`,
      )
    },
    onNotificationAction: (notificationId: string) => {
      const notification = SAMPLE_NOTIFICATIONS.find(
        (item) => item.id === notificationId,
      )
      setLastAction(
        `Action: ${notification?.actionButtonText ?? notificationId}`,
      )
    },
    userEmail: 'jamie.rivera@example.com',
    userMenuItems,
  }

  return (
    <>
      <UiPreviewCanvas
        title="Header"
        description="Brand, date, search, HIPAA view, location, notifications, and account in light and dark."
        stageClassName="ui-preview__stage--wide header-showcase__stage-wrap"
      >
        <div className="header-showcase__sheet">
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click location, notifications, or account to open working
              dropdowns. The notifications panel mirrors the production header
              with tabs, filters, and per-item actions.
            </p>
            <div className="header-showcase__stage header-showcase__stage--live">
              <Header
                logo={<ToothLogo />}
                date={SAMPLE_DATE}
                showSearch={showSearch}
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                showHipaaToggle={showHipaaToggle}
                hipaaView={hipaaView}
                onHipaaViewChange={setHipaaView}
                showLocation={showLocation}
                locationName={selectedLocation?.name ?? 'XYZ Clinic'}
                showNotifications={showNotifications}
                hasNotifications={hasNotifications}
                showUser
                userName="Jamie Rivera"
                {...dropdownHeaderProps}
              />
            </div>
            <p className="header-showcase__feedback">{lastAction}</p>
          </section>

          <div className="header-showcase__section-header">
            <span className="header-showcase__section-label">Variants</span>
          </div>

          <div className="header-showcase__variants">
            <div className="header-showcase__variant">
              <span className="header-showcase__variant-label">Light</span>
              <div className="header-showcase__stage">
                <Header
                  logo={<ToothLogo />}
                  date={SAMPLE_DATE}
                  locationName="XYZ Clinic"
                  userName="Jamie Rivera"
                  {...dropdownHeaderProps}
                />
              </div>
            </div>

            <div className="header-showcase__variant">
              <span className="header-showcase__variant-label">
                Light · notifications
              </span>
              <div className="header-showcase__stage">
                <Header
                  logo={<ToothLogo />}
                  date={SAMPLE_DATE}
                  locationName="XYZ Clinic"
                  userName="Jamie Rivera"
                  hasNotifications
                  {...dropdownHeaderProps}
                />
              </div>
            </div>

            <div className="header-showcase__variant">
              <span className="header-showcase__variant-label">Dark</span>
              <div className="header-showcase__stage">
                <Header
                  logo={<ToothLogo />}
                  date={SAMPLE_DATE}
                  locationName="XYZ Clinic"
                  userName="Jamie Rivera"
                  {...dropdownHeaderProps}
                />
              </div>
            </div>

            <div className="header-showcase__variant">
              <span className="header-showcase__variant-label">
                Dark · notifications
              </span>
              <div className="header-showcase__stage">
                <Header
                  logo={<ToothLogo />}
                  date={SAMPLE_DATE}
                  locationName="XYZ Clinic"
                  userName="Jamie Rivera"
                  hasNotifications
                  {...dropdownHeaderProps}
                />
              </div>
            </div>
          </div>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Header" sections={propertySections} />
    </>
  )
}

export default HeaderShowcase
